import { api, getToken } from "@/lib/api/client";
import type {
  DashboardBucketsResponse,
  DisputeStatus,
  FullDetailResponse,
  MonthlyReport,
  ProductorDetailResponse,
  ProductoresListResponse,
} from "./types";

// Prefix /api (settlements.routes en app.ts:633)
// Prefix /api/matrix (en app.ts:632)

export const settlementsApi = {
  // ── Dashboard buckets — 1 fetch = 5 sub-tabs ──────────────────────
  dashboardBuckets: async (): Promise<DashboardBucketsResponse> => {
    const { data } = await api.get<DashboardBucketsResponse>(
      "/api/matrix/settlements/dashboard-buckets",
    );
    return data;
  },

  fullDetail: async (id: number): Promise<FullDetailResponse> => {
    const { data } = await api.get<FullDetailResponse>(
      `/api/matrix/settlements/${id}/full`,
    );
    return data;
  },

  // ── Acciones críticas ─────────────────────────────────────────────
  resolveDispute: async (
    settlementId: number,
    body: {
      disputeId: number;
      statusDisputa: DisputeStatus;
      minutaResolucion: string;
      montoAjuste: number;
      confirmLargeAdjustment?: boolean;
    },
  ): Promise<{ message: string; settlement: { id: number; status: string; amountToTransfer: number } }> => {
    const { data } = await api.post(
      `/api/matrix/settlements/${settlementId}/resolve-dispute`,
      body,
    );
    return data;
  },

  markPendingTransfer: async (id: number): Promise<void> => {
    await api.post(`/api/matrix/settlements/${id}/mark-pending-transfer`);
  },

  /**
   * Genera y finaliza la liquidación de un evento terminado — misma vía que
   * el cron auto-finalize: crea el settlement, arranca la ventana de revisión
   * de 3 días y MANDA EMAIL al productor. Permiso settlements:generate
   * (el backend rechaza con 403 si no lo tienes). Ruta sin /matrix:
   * settlements.routes.ts monta /api/settlements/event/:id/finalize.
   */
  finalizeEvent: async (
    eventId: number,
  ): Promise<{ message?: string; settlementId?: number }> => {
    const { data } = await api.post(`/api/settlements/event/${eventId}/finalize`);
    return data;
  },

  /**
   * Marca como transferida. Multipart con hasta 5 archivos.
   * Subimos via fetch directo porque axios + FormData con multi-field
   * a veces da problemas con Content-Type.
   */
  markTransferred: async (
    settlementId: number,
    body: {
      numeroOperacion: string;
      fechaTransferencia: string;
      bancoEmisor: string;
      notas?: string;
    },
    files: File[],
  ): Promise<{ message: string; settlement: { id: number; status: string }; transferRecordId: number }> => {
    const formData = new FormData();
    formData.append("numeroOperacion", body.numeroOperacion);
    formData.append("fechaTransferencia", body.fechaTransferencia);
    formData.append("bancoEmisor", body.bancoEmisor);
    if (body.notas) formData.append("notas", body.notas);
    files.forEach((f) => formData.append("files", f, f.name));

    const base = import.meta.env.VITE_API_BASE || "https://api-nexus.mixcore.club";
    const token = getToken();
    const res = await fetch(`${base}/api/matrix/settlements/${settlementId}/mark-transferred`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }
    return res.json();
  },

  markTransferFailed: async (id: number, reason: string): Promise<void> => {
    await api.post(`/api/matrix/settlements/${id}/mark-transfer-failed`, { reason });
  },

  // ── Productores ───────────────────────────────────────────────────
  listProductores: async (): Promise<ProductoresListResponse> => {
    const { data } = await api.get<ProductoresListResponse>("/api/matrix/productores");
    return data;
  },

  productorDetail: async (id: number): Promise<ProductorDetailResponse> => {
    const { data } = await api.get<ProductorDetailResponse>(`/api/matrix/productores/${id}`);
    return data;
  },

  // ── Reportes ──────────────────────────────────────────────────────
  monthlyReport: async (month: string): Promise<MonthlyReport> => {
    const { data } = await api.get<MonthlyReport>("/api/matrix/settlements/monthly-report", {
      params: { month },
    });
    return data;
  },
};
